import { useMatchMedia } from '@ati/hooks';
import cn from 'clsx';
import { observer } from 'mobx-react-lite';

import { QuestionTooltip } from 'components/QuestionTooltip';
import { loadsSearchFAQ, notificationsLimitFAQ } from 'constants/pages';
import { columnsCount, mediaQuery } from 'constants/ui';
import { useStore } from 'hooks/useStore';
import { TabsContent } from 'layout/TabsContent';
import { QueryType } from 'store/QueryStore/QueryStore';
import { TSubscribedFilter } from 'store/Subscriptions/SubscribedFilter';
import { SavedFilter } from 'store/TabsDataStore/TabsDataStore';
import { tabIds } from 'store/UIStore/UIStore';
import { interpolate } from 'utils/string/format';

import { SavedFiltersIcon } from './SavedFiltersIcon';

import css from './SavedFilters.module.css';

const SavedFilters = (props: { className?: string }) => {
  const {
    app: { i18n, env, fillFilterFromQuery, loading, lsIsAdaptive },
    query,
    tabsData,
    ui,
    subscriptions: { counters },
    savedFilters,
  } = useStore();

  const handleSubscriptionClick = (filter: SavedFilter) => {
    savedFilters.openFilterPopup(filter, { enableSubscription: true });
  };

  const handleFilterClick = (filter: SavedFilter) => {
    ui.setActiveTab(tabIds.search);
    tabsData.selectFilter(filter);
    query.setParsedQueryData(filter.filter);
    query.setType(QueryType.byFilter);
    fillFilterFromQuery({ skipQueryParsing: true });
    // todo сравнить хэш заполненного фильтра и сохранённого. Если они не совпадают — написал ошибку в лог
  };

  const handleFilterDelete = (filter: SavedFilter) => {
    tabsData.deleteSavedFilter(filter.id);
  };

  const mobile = useMatchMedia(mediaQuery.mobile);
  const columns = mobile && lsIsAdaptive ? columnsCount.mobile : columnsCount.desktop;

  const notificationMessage = () => {
    if (counters?.isNotificationsLimitReached) {
      return interpolate(i18n.savedFilters.notificationsInfo.limitReached, {
        max: String(counters?.dailyNotificationsLimit),
      });
    }

    const notificationDesc = mobile
      ? i18n.savedFilters.notificationsInfo.desc.short
      : i18n.savedFilters.notificationsInfo.desc.full;

    return interpolate(notificationDesc, {
      from: String(counters?.sentNotificationsCount || 0),
      to: String(counters?.dailyNotificationsLimit || 3000),
    });
  };

  return (
    <TabsContent<TSubscribedFilter>
      activeItem={tabsData.selectedFilter}
      className={props.className}
      columns={columns}
      header={
        <TabsContent.Header>
          <span className={cn(css.notifications, { [css.warning]: counters?.isNotificationsLimitReached })}>
            {notificationMessage()}
            <a href={interpolate(notificationsLimitFAQ, { env })} rel="noreferrer" target="_blank">
              <QuestionTooltip text={i18n.savedFilters.notificationsInfo.tooltip} tooltipWidth={150} />
            </a>
          </span>

          <a
            className="glz-link glz-is-gray glz-is-underline"
            href={interpolate(loadsSearchFAQ, { env })}
            rel="noreferrer"
            target="_blank"
          >
            {i18n.app.moreOnFilters.firstPart}{' '}
            <span className={css.linkTextSecondPart}>{i18n.app.moreOnFilters.secondPart}</span>
          </a>
        </TabsContent.Header>
      }
      isLoading={loading.deleteSavedFilter || loading.tabsData}
      items={tabsData.savedFiltersData || []}
      itemsCounter="filter"
      noContentElement={
        <TabsContent.NoContent
          actionElement={
            <a
              className="glz-link glz-is-underline"
              href={interpolate(loadsSearchFAQ, { env })}
              rel="noreferrer"
              target="_blank"
            >
              {i18n.savedFilters.noContent.actionElement.text}
            </a>
          }
          desc={i18n.savedFilters.noContent.desc}
          icon={<SavedFiltersIcon />}
          title={i18n.savedFilters.noContent.title}
        />
      }
      onItemClick={handleFilterClick}
      onItemDelete={handleFilterDelete}
      onItemSubscribe={handleSubscriptionClick}
    />
  );
};

const connected = observer(SavedFilters);

export { connected as SavedFilters };
